/**
 * W4 — Cost plan / AFE lines. The PLAN-authoring screen for a plan version's
 * WBS×CBS cost lines: a WBS (program step) × CBS (chart-of-accounts) grid where
 * the planner authors budget / accrual method / unit / planned qty / rate.
 * Built to w4-cost-afe-lines.html on the P3 kit.
 *
 * WRITE BOUNDARY (oxbo-w-zsplit-and-endpoints.md §2): Oxbo-W owns the PLAN
 * columns only — budget, accrual_method, unit, planned_qty, rate, program_step,
 * status. committed / accrued (devengado) / actual / variance are compute &
 * Oxbo-Z owned; they are not fields on `AfeLineRow` and are never rendered as
 * editable here (the read view of plan-vs-actual lives in Oxbo-Z). So the grid
 * physically cannot author a non-plan column — the boundary is a type invariant,
 * not just a disabled input.
 *
 * Data flow: the active plan (wellContext.activePlanId, falling back to the
 * seeded demo plan) → getPlan (afe/currency) + getPlanDatasets (afe_lines +
 * programa for the step rail). Edits are held locally and committed with a
 * single putAfeLines PUT; the left rail filters the grid by program step.
 */
import { useMemo, useState } from "react";
import { useAppSelector } from "@app/hooks";
import { useLocale } from "@hooks/useLocale";
import { useMoney } from "@units/useMoney";
import { useGetPlanQuery, useGetPlanDatasetsQuery, usePutAfeLinesMutation } from "@api/pApi";
import type { AccrualMethod, AfeLineRow, ProgramaStep } from "@api/types";
import { CatalogPicker } from "@components/catalog/CatalogPicker";
import { InlineRowGrid, type GridColumn } from "@components/input/InlineRowGrid";
import { QueryState } from "@components/ui/QueryState";
import { asArray } from "@lib/asArray";
import {
  ACCRUAL_METHODS,
  accrualMeta,
  lineBudget,
  linesForStep,
  totalBudget,
  totalsByPhase,
  withDerivedBudget,
} from "./costModel";
import "./cost.css";

/**
 * Demo fallback plan. In standalone mode the finder → workspace flow sets
 * activePlanId; when the screen is opened cold we point at the fully-seeded
 * FP-1216 unconventional drilling plan the wireframe is drawn from.
 */
const DEMO_PLAN_ID = "pl-fp1216-pnc";

export function CostPlan() {
  const { t } = useLocale();
  const { money } = useMoney();

  const activePlanId = useAppSelector((s) => s.wellContext.activePlanId);
  const planId = activePlanId ?? DEMO_PLAN_ID;

  const planQ = useGetPlanQuery(planId);
  const dsQ = useGetPlanDatasetsQuery(planId);

  const currency = planQ.data?.currency ?? "USD";
  // asArray at the ingestion boundary: a real backend can return a
  // present-but-non-array body ({}, {afe_lines:{}}, {programa:null}); `?? []`
  // does NOT guard those, so the downstream .map/.filter/.reduce would throw
  // and white-screen (the Oxbo-Z prod crash class). See @lib/asArray.
  const programa = useMemo(() => asArray<ProgramaStep>(dsQ.data?.programa), [dsQ.data]);

  // Local working copy of the AFE lines; seeded from the server, committed on Save.
  const serverRows = useMemo(() => asArray<AfeLineRow>(dsQ.data?.afe_lines), [dsQ.data]);
  const [draft, setDraft] = useState<AfeLineRow[] | null>(null);
  const rows = draft ?? serverRows;
  const dirty = draft !== null;

  const [selectedStep, setSelectedStep] = useState<number | null>(null); // null = all steps
  const [putAfeLines, putState] = usePutAfeLinesMutation();

  const money0 = (n: number | null | undefined) => money(n, currency);

  const visibleRows = useMemo(() => linesForStep(rows, selectedStep), [rows, selectedStep]);
  const phaseTotals = useMemo(() => totalsByPhase(rows), [rows]);
  const grandTotal = useMemo(() => totalBudget(rows), [rows]);
  const maxPhase = useMemo(
    () => phaseTotals.reduce((m, p) => Math.max(m, p.budget), 0),
    [phaseTotals],
  );

  /** Map a program step seq → its phase label (for stamping new lines). */
  const phaseForStep = (seq: number | null): string | null =>
    seq == null ? null : (programa.find((p) => p.seq === seq)?.phase ?? null);

  function commit(next: AfeLineRow[]) {
    setDraft(next);
  }

  function makeEmptyRow(): AfeLineRow {
    const step = selectedStep;
    return {
      seq: rows.reduce((m, r) => Math.max(m, r.seq), 0) + 1, // next free seq
      account: "",
      account_description: null,
      phase: phaseForStep(step),
      program_step: step,
      unit: null,
      planned_qty: null,
      rate: null,
      budget: null,
      accrual_method: "avance_etapas",
      material: null,
      code: null,
    };
  }

  async function save() {
    if (!dirty) return;
    // S3: preserve each line's own seq (don't re-stamp by array position — the
    // grid is often step-filtered, so position ≠ identity and re-stamping would
    // reorder lines non-deterministically). S2: stamp budget = qty × rate so the
    // persisted value matches what the grid shows (single source of truth).
    const rowsToSave = rows.map(withDerivedBudget);
    await putAfeLines({ planId, rows: rowsToSave }).unwrap();
    setDraft(null); // reload from the (now-updated) server cache
  }

  const columns: GridColumn<AfeLineRow>[] = [
    {
      key: "account",
      label: t("cost.col.account"),
      width: 220,
      render: (row, update) => (
        <CatalogPicker
          categoryKey="accounts"
          value={row.account || null}
          onChange={(key, label) =>
            update({ account: key ?? "", account_description: label ?? row.account_description })
          }
          placeholder={t("cost.account.placeholder")}
        />
      ),
    },
    {
      key: "description",
      label: t("cost.col.description"),
      width: 180,
      render: (row, update) => (
        <input
          type="text"
          className="cell-input"
          value={row.account_description ?? ""}
          placeholder={t("cost.col.description")}
          aria-label={t("cost.col.description")}
          onChange={(e) => update({ account_description: e.target.value || null })}
        />
      ),
    },
    {
      key: "program_step",
      label: t("cost.col.programStep"),
      width: 150,
      render: (row, update) => (
        <select
          className="cell-input"
          value={row.program_step ?? ""}
          aria-label={t("cost.col.programStep")}
          onChange={(e) => {
            const seq = e.target.value === "" ? null : Number(e.target.value);
            update({ program_step: seq, phase: phaseForStep(seq) ?? row.phase });
          }}
        >
          <option value="">{t("cost.step.global")}</option>
          {programa.map((p) => (
            <option key={p.seq} value={p.seq}>
              {p.seq} · {p.phase}
            </option>
          ))}
        </select>
      ),
    },
    {
      key: "accrual",
      label: t("cost.col.accrual"),
      width: 160,
      render: (row, update) => {
        const meta = accrualMeta(row.accrual_method);
        return (
          <div className="accrual-cell">
            <select
              className="cell-input am-select"
              value={row.accrual_method}
              aria-label={t("cost.col.accrual")}
              onChange={(e) => update({ accrual_method: e.target.value as AccrualMethod })}
            >
              {ACCRUAL_METHODS.map((m) => (
                <option key={m.value} value={m.value}>
                  {t(m.labelKey)}
                </option>
              ))}
            </select>
            <span
              className="am-badge"
              style={{ color: `var(${meta.tokenVar})`, background: `color-mix(in srgb, var(${meta.tokenVar}) 14%, transparent)` }}
            >
              {t(meta.labelKey)}
            </span>
          </div>
        );
      },
    },
    {
      key: "unit",
      label: t("cost.col.unit"),
      width: 70,
      render: (row, update) => (
        <input
          type="text"
          className="cell-input unit-input"
          value={row.unit ?? ""}
          placeholder={t("cost.unit.placeholder")}
          aria-label={t("cost.col.unit")}
          onChange={(e) => update({ unit: e.target.value || null })}
        />
      ),
    },
    {
      key: "qty",
      label: t("cost.col.qty"),
      width: 84,
      numeric: true,
      render: (row, update) => (
        <input
          type="number"
          className="cell-input num-input"
          step="any"
          value={row.planned_qty ?? ""}
          placeholder="0"
          aria-label={t("cost.col.qty")}
          onChange={(e) =>
            update({ planned_qty: e.target.value === "" ? null : Number(e.target.value) })
          }
        />
      ),
    },
    {
      key: "rate",
      label: t("cost.col.rate"),
      width: 100,
      numeric: true,
      render: (row, update) => (
        <input
          type="number"
          className="cell-input num-input"
          step="any"
          value={row.rate ?? ""}
          placeholder="0"
          aria-label={t("cost.col.rate")}
          onChange={(e) =>
            update({ rate: e.target.value === "" ? null : Number(e.target.value) })
          }
        />
      ),
    },
    {
      key: "budget",
      label: t("cost.col.budget"),
      width: 120,
      numeric: true,
      render: (row) => (
        <span className="budget-cell tab-nums" title={t("cost.budget.derived")}>
          {money0(lineBudget(row))}
        </span>
      ),
    },
  ];

  const stepTotal = totalBudget(visibleRows);

  return (
    <div className="cost-screen">
      <div className="screen-head">
        <h1>{t("cost.title")}</h1>
        <div className="chip-ctx">
          <span className="k">{t("cost.ctx.well")}</span>
          <span className="mono">{planQ.data?.well_uwi ?? "—"}</span>
          <span className="dim">·</span>
          <span className="k">{t("cost.ctx.afe")}</span>
          <span className="mono">{planQ.data?.afe_no ?? "—"}</span>
          <span className="dim">·</span>
          <span>
            {t("cost.ctx.total")} <span className="tab-nums">{money0(planQ.data?.authorized_total ?? grandTotal)}</span>
          </span>
          <span className={`badge ${planQ.data?.status === "published" ? "closed" : "open"}`}>
            {t(`plan.status.${planQ.data?.status ?? "draft"}` as const)}
          </span>
        </div>
        <div className="actions">
          <button
            type="button"
            className="btn primary"
            onClick={() => void save()}
            disabled={!dirty || putState.isLoading}
          >
            <span className="ic ic-check" aria-hidden="true" />
            {putState.isLoading ? t("cost.saving") : t("cost.save")}
          </button>
        </div>
      </div>

      <div className="dim cost-lede">{t("cost.lede")}</div>

      {/* Write-boundary note: this screen authors the plan; actuals live in Z. */}
      <div className="boundary-note">
        <span className="qm" aria-hidden="true">i</span>
        <span>{t("cost.boundary")}</span>
      </div>

      <QueryState
        isLoading={dsQ.isLoading || planQ.isLoading}
        isError={dsQ.isError || planQ.isError}
        onRetry={() => void dsQ.refetch()}
      >
        <div className="cost-body">
          {/* LEFT: program-step rail (WBS) */}
          <aside className="card rail-card">
            <h2>{t("cost.rail.title")}</h2>
            <div className="dim rail-hint">{t("cost.rail.hint")}</div>
            <div className="steps" role="tablist" aria-label={t("cost.rail.title")}>
              <button
                type="button"
                className={`step${selectedStep === null ? " active" : ""}`}
                role="tab"
                aria-selected={selectedStep === null}
                onClick={() => setSelectedStep(null)}
              >
                <span className="idx">Σ</span>
                <span className="step-body">
                  <span className="name">{t("cost.step.all")}</span>
                  <span className="meta">{t("cost.step.allMeta", { n: rows.length })}</span>
                </span>
                <span className="amt">{money0(grandTotal)}</span>
              </button>
              {programa.map((p) => {
                const stepTot = totalBudget(rows.filter((r) => r.program_step === p.seq));
                return (
                  <button
                    key={p.seq}
                    type="button"
                    className={`step${selectedStep === p.seq ? " active" : ""}`}
                    role="tab"
                    aria-selected={selectedStep === p.seq}
                    onClick={() => setSelectedStep(p.seq)}
                  >
                    <span className="idx">{p.seq}</span>
                    <span className="step-body">
                      <span className="name">{p.phase}</span>
                      <span className="meta">
                        {p.depth_from != null && p.depth_until != null
                          ? `${p.depth_from}–${p.depth_until} m`
                          : (p.code_description ?? "")}
                      </span>
                    </span>
                    <span className="amt">{money0(stepTot)}</span>
                  </button>
                );
              })}
            </div>
          </aside>

          {/* MAIN: the AFE-line grid (WBS×CBS), filtered by the selected step */}
          <div className="cost-main">
            <h2 className="grid-head">
              {t("cost.grid.title")}{" "}
              <span className="dim grid-sub">
                {selectedStep === null
                  ? t("cost.grid.subAll")
                  : `— ${selectedStep} · ${phaseForStep(selectedStep) ?? ""}`}
              </span>
            </h2>

            <InlineRowGrid<AfeLineRow>
              rows={visibleRows}
              columns={columns}
              readOnly={planQ.data?.status === "locked" || planQ.data?.status === "published"}
              addLabel={t("cost.addLine")}
              emptyLabel={t("cost.empty")}
              makeEmptyRow={makeEmptyRow}
              onChange={(nextVisible) => {
                // Merge the (filtered) visible rows back into the full set.
                if (selectedStep === null) {
                  commit(nextVisible);
                } else {
                  const others = rows.filter((r) => r.program_step !== selectedStep);
                  commit([...others, ...nextVisible]);
                }
              }}
              footer={
                <div className="grid-footer">
                  <span className="lbl">
                    {selectedStep === null ? t("cost.total.all") : t("cost.total.step")}
                  </span>
                  <span className="val tab-nums">{money0(stepTotal)}</span>
                </div>
              }
            />
          </div>
        </div>

        {/* BOTTOM: totals by phase (WBS rollup) */}
        <section className="card phase-card">
          <div className="phase-head">
            <h2>{t("cost.phase.title")}</h2>
            <span className="dim">{currency}</span>
          </div>
          <div className="phasebars">
            {phaseTotals.map((pt) => (
              <div className="pbar-row" key={pt.phase}>
                <span className="badge info phase-name">{pt.phase}</span>
                <div className="pbar-track">
                  <div
                    className="pbar-seg"
                    style={{
                      width: maxPhase > 0 ? `${(pt.budget / maxPhase) * 100}%` : "0%",
                      background: "var(--oxbo-m)",
                    }}
                  />
                </div>
                <span className="pbar-val tab-nums">{money0(pt.budget)}</span>
              </div>
            ))}
            {phaseTotals.length === 0 && <div className="dim">{t("cost.empty")}</div>}
          </div>
          <div className="phase-total">
            <span>{t("cost.total.all")}</span>
            <span className="tab-nums">{money0(grandTotal)}</span>
          </div>
        </section>
      </QueryState>
    </div>
  );
}
